import React, {useEffect, useState} from "react";
import LabeledSection from "../../../../components/Layout/Page/LabeledSection";
import {PermissionLevel} from "../../../../app/api/model/user";
import {
    useDefaultFirmwareListQuery,
    useDefaultFirmwareSetMutation,
    useHardwareTypeListQuery,
    useSessionQuery,
} from "../../../../app/api/api";
import {useTranslation} from "react-i18next";
import {Grid} from "@mui/material";
import {LabeledValue} from "../../../../components/Layout/Page";
import {Firmware, FirmwareField, FirmwareState} from "../../../../app/api/model/firmware";
import {FilterOperator} from "../../../../components/ExTable";
import {getTranslation, TranslationContext} from "../../../../utils/getTranslationKey";
import {FirmwareAutocomplete} from "../../../../components/input/AsyncAutocomplete/variants";
import {EMPTY_UI} from "../../../../variables";

enum Environment {
    DKV_LIVE = "dkvLive",
    LEGACY = "legacy",
}

interface FirmwareMapping {
    [hardwareType: string]: {
        [Environment.DKV_LIVE]?: Firmware;
        [Environment.LEGACY]?: Firmware;
    };
}

export default function StageDefaults() {
    const {t} = useTranslation();
    const {data: session} = useSessionQuery();
    const [editTarget, setEditTarget] = useState<string | undefined>();
    const [firmwareMapping, setFirmwareMapping] = useState<FirmwareMapping | undefined>();
    const {data: hardwareTypeListResponse} = useHardwareTypeListQuery({});
    const {data: defaultFirmwareListResponse} = useDefaultFirmwareListQuery({});
    const [setFirmwareAsDefault] = useDefaultFirmwareSetMutation();

    const hardwareTypes = hardwareTypeListResponse?.payload;

    const handleSetFirmwareAsDefault = (value?: Firmware) => {
        if (value) {
            setFirmwareAsDefault(value);
            setEditTarget(undefined);
        }
    };

    useEffect(() => {
        if (defaultFirmwareListResponse?.payload) {
            const newFirmwareMapping: FirmwareMapping = {};
            defaultFirmwareListResponse.payload.forEach((firmware) => {
                if (!newFirmwareMapping?.[firmware.hardwareType]) {
                    newFirmwareMapping[firmware.hardwareType] = {
                        [firmware.legacy ? Environment.LEGACY : Environment.DKV_LIVE]: firmware,
                    };
                } else {
                    newFirmwareMapping[firmware.hardwareType][
                        firmware.legacy ? Environment.LEGACY : Environment.DKV_LIVE
                    ] = firmware;
                }
            });
            setFirmwareMapping(newFirmwareMapping);
        }
    }, [JSON.stringify(defaultFirmwareListResponse?.payload)]);

    const getLabeledField = (hardwareType: string, legacy: boolean) => {
        const environment = legacy ? Environment.LEGACY : Environment.DKV_LIVE;
        const firmware = firmwareMapping?.[hardwareType]?.[environment];
        return (
            <LabeledValue
                label={getTranslation({context: TranslationContext.ENVIRONMENT, value: environment, t})}
                content={firmware?.[FirmwareField.VERSION_NUMBER] || EMPTY_UI}
                onEdit={
                    session?.permissionLevel === PermissionLevel.ADMIN
                        ? () => setEditTarget(`${hardwareType},${legacy}`)
                        : undefined
                }
                md={6}
                xs={12}
                sx={{marginTop: "10px"}}
            />
        );
    };

    const getEditField = (hardwareType: string, legacy: boolean) => {
        const environment = legacy ? Environment.LEGACY : Environment.DKV_LIVE;
        const firmware = firmwareMapping?.[hardwareType]?.[environment];
        return (
            <Grid item md={6} xs={12} sx={{marginTop: "10px"}}>
                <FirmwareAutocomplete
                    id={`${hardwareType},${legacy}`}
                    label={getTranslation({context: TranslationContext.ENVIRONMENT, value: environment, t})}
                    value={firmware}
                    additionalFilters={[
                        {f: "hardwareType", o: FilterOperator.EQ, v: hardwareType},
                        {f: "legacy", o: FilterOperator.EQ, v: String(legacy)},
                        {f: "state", o: FilterOperator.EQ, v: FirmwareState.STABLE},
                        {
                            f: "s3Key",
                            o: FilterOperator.NOT,
                            v: "",
                        },
                    ]}
                    onChange={handleSetFirmwareAsDefault}
                    sx={{maxWidth: 400}}
                />
            </Grid>
        );
    };
    return (
        <>
            {hardwareTypes?.map((hardwareType) => (
                <LabeledSection key={hardwareType} title={hardwareType}>
                    <Grid container spacing={4}>
                        {editTarget === `${hardwareType},false`
                            ? getEditField(hardwareType, false)
                            : getLabeledField(hardwareType, false)}
                        {editTarget === `${hardwareType},true`
                            ? getEditField(hardwareType, true)
                            : getLabeledField(hardwareType, true)}
                    </Grid>
                </LabeledSection>
            ))}
        </>
    );
}
